
import React, { useState, useEffect } from 'react';
import { UserProfile } from '../types';
import { CITIES, ACTIVITY_SPHERES } from '../constants';
import { supabase } from '../services/supabaseClient';

interface ProfileFormProps {
  user: UserProfile | null;
  onSave: (profile: UserProfile) => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({ user, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<UserProfile>(user || {
    id: crypto.randomUUID(), name: '', photo: '', tgHandle: '', tgChatId: '', password: '', city: CITIES[0], 
    activitySphere: ACTIVITY_SPHERES[0], interests: [], goal: 'both', format: 'both', aboutMe: '', 
    canHelpWith: '', lookingFor: '', role: 'user', visibility: 'community_only'
  });
  const [formData, setFormData] = useState<UserProfile>(initialData);

  const isDirty = JSON.stringify(initialData) !== JSON.stringify(formData);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { data, error } = await supabase.storage.from('avatars').upload(`${crypto.randomUUID()}`, file);
    if (!error) {
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(data.path);
      setFormData(prev => ({ ...prev, photo: publicUrl }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isDirty) return;
    setLoading(true);
    const payload = { 
      ...formData, 
      tg_handle: formData.tgHandle.startsWith('@') ? formData.tgHandle : `@${formData.tgHandle}` 
    };
    const { error } = await supabase.from('profiles').upsert(payload);
    if (!error) {
      setInitialData(formData);
      onSave(formData);
    }
    setLoading(false);
  };

  const labelClasses = "block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1";
  const inputClasses = "w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:ring-2 focus:ring-black font-bold text-sm transition-all disabled:opacity-50";

  return (
    <div className="max-w-5xl mx-auto py-12 px-4">
      <form onSubmit={handleSubmit} className="bg-white rounded-[4rem] p-12 md:p-20 shadow-2xl border border-gray-50 flex flex-col md:flex-row gap-16 animate-fadeIn">
        <div className="md:w-1/3 flex flex-col items-center gap-8">
           <div className="w-full aspect-square rounded-[3rem] bg-gray-50 border-2 border-dashed border-gray-200 overflow-hidden relative group cursor-pointer" onClick={() => document.getElementById('avatar-input')?.click()}>
              {formData.photo ? <img src={formData.photo} className="w-full h-full object-cover" /> : <div className="h-full flex items-center justify-center opacity-20 text-6xl">👤</div>}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                <span className="text-white text-[10px] font-black uppercase tracking-widest">Загрузить фото</span>
              </div>
           </div>
           <input type="file" id="avatar-input" className="hidden" accept="image/*" onChange={handleFileUpload} />
           
           <div className="w-full space-y-4">
              <div className={`p-6 rounded-3xl border ${formData.bot_connected ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${formData.bot_connected ? 'text-green-600' : 'text-red-600'}`}>
                  {formData.bot_connected ? '✅ Бот подключен' : '❌ Бот не подключен'}
                </p>
                <p className="text-[10px] text-gray-500 font-bold mb-3 uppercase tracking-tighter leading-tight">Подключите уведомления, чтобы не пропустить приглашения на кофе.</p>
                <a href="https://t.me/RCVietnamBot" target="_blank" className="text-[10px] font-black text-black underline uppercase">Открыть бота</a>
              </div>
              
              <div><label className={labelClasses}>Telegram Handle</label><input type="text" className={inputClasses} value={formData.tgHandle} onChange={e => setFormData({...formData, tgHandle: e.target.value})} placeholder="username" /></div>
              <div><label className={labelClasses}>Telegram Chat ID</label><input type="text" className={inputClasses} value={formData.tgChatId || ''} onChange={e => setFormData({...formData, tgChatId: e.target.value})} placeholder="Введите ID из бота" /></div>
              <div><label className={labelClasses}>Пароль для входа</label><input type="password" className={inputClasses} value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="••••••••" /></div>
           </div>
        </div>

        <div className="md:w-2/3 space-y-8">
           <div className="grid grid-cols-2 gap-6">
              <div><label className={labelClasses}>Имя</label><input type="text" className={inputClasses} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></div>
              <div><label className={labelClasses}>Город</label><select className={inputClasses} value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})}>{CITIES.map(c => <option key={c}>{c}</option>)}</select></div>
           </div>
           <div><label className={labelClasses}>Сфера деятельности</label><select className={inputClasses} value={formData.activitySphere} onChange={e => setFormData({...formData, activitySphere: e.target.value})}>{ACTIVITY_SPHERES.map(s => <option key={s}>{s}</option>)}</select></div>
           <div><label className={labelClasses}>О себе</label><textarea className={`${inputClasses} h-24 resize-none`} value={formData.aboutMe} onChange={e => setFormData({...formData, aboutMe: e.target.value})} /></div>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div><label className={labelClasses}>Чем полезен</label><textarea className={`${inputClasses} h-40 resize-none border-green-50 bg-green-50/5`} value={formData.canHelpWith} onChange={e => setFormData({...formData, canHelpWith: e.target.value})} placeholder="Напр: Консультации по налогам..." /></div>
              <div><label className={labelClasses}>Что ищу</label><textarea className={`${inputClasses} h-40 resize-none border-red-50 bg-red-50/5`} value={formData.lookingFor} onChange={e => setFormData({...formData, lookingFor: e.target.value})} placeholder="Напр: Разработчиков для стартапа..." /></div>
           </div>
           
           <button 
             type="submit" 
             disabled={!isDirty || loading} 
             className={`w-full py-7 font-black rounded-3xl uppercase tracking-widest text-xs transition-all shadow-2xl ${isDirty ? 'bg-black text-white hover:bg-red-600' : 'bg-gray-100 text-gray-300'}`}
           >
             {loading ? 'СОХРАНЕНИЕ...' : isDirty ? 'СОХРАНИТЬ АНКЕТУ' : 'ИЗМЕНЕНИЙ НЕТ'}
           </button>
        </div>
      </form>
    </div>
  );
};
